import React, {useEffect, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {DataFieldType} from 'yougile-model'
import {InputNew} from '@components/common/base/input-new'
import {CustomFieldRow} from './field-row'

interface Props {
  label: string
  value: string | undefined
  actions?: React.ReactNode
  onConfirm: (val: string) => void
  type?: DataFieldType
  allowEmpty?: boolean
}

const validators: {[K in DataFieldType]: (val: string) => boolean} = {
  string: () => true,
  number: val => /^\d*$/.test(val.trim()),
  email: val => Util.isEmail(val.trim()),
  url: val => {
    const raw = val.trim()
    const normalized = /^(https?:)?\/\//i.test(raw) ? raw : `https://${raw}`

    try {
      const url = new URL(normalized)
      return url.hostname.includes('.')
    } catch {
      return false
    }
  }
}

const inputHtmlType: {[K in DataFieldType]: string} = {
  string: 'text',
  number: 'text',
  email: 'email',
  url: 'url'
}

const getDataFieldValidationErrorMessage = (type: DataFieldType): string => {
  const translations = {
    string: '',
    number: Lang.msg.errorOnlyDigitsAllowed,
    email: Lang.msg.errorInvalidEmail,
    url: Lang.msg.errorInvalidURL
  }
  return translations[type]
}

export const CustomFieldInput = ({
  label,
  value,
  actions,
  onConfirm,
  type = 'string',
  allowEmpty = true
}: Props) => {
  const [editedValue, setEditedValue] = useState(value ?? '')
  const [isValid, setIsValid] = useState(true)
  const inputRef = useRef<HTMLInputElement>(null)
  const originalValue = useRef(value ?? '')

  const isEdited = editedValue !== value

  const validate = (val: string) => {
    const validator = validators[type] ?? validators.string
    const normalizedValue = val.trim()
    const len = normalizedValue.length

    setIsValid(
      (allowEmpty && len === 0) || (len > 0 && validator(normalizedValue))
    )
  }

  const handleChange = (val: string) => {
    setEditedValue(val)
    validate(val)
  }

  useEffect(() => {
    handleChange(value)
  }, [value])

  return (
    <CustomFieldRow label={label}>
      <InputNew
        ref={inputRef}
        className="flex-1"
        inputClassName={
          !isValid
            ? ''
            : 'shadow-none hover:shadow-border-1 hover:shadow-line-base focus:shadow-border-1'
        }
        value={editedValue}
        onChange={handleChange}
        type={inputHtmlType[type]}
        error={!isValid}
        support={
          !isValid ? getDataFieldValidationErrorMessage(type) : undefined
        }
        placeholder={Lang.msg.empty}
        onBlur={() => {
          if (isEdited && isValid) {
            onConfirm(editedValue)
          }
        }}
        iconRight={!isValid ? 'IconX' : undefined}
        onIconRightClick={() => {
          handleChange(originalValue.current)
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' && isEdited && isValid) {
            onConfirm(editedValue)
            inputRef?.current.blur()
          }
        }}
      />
      {actions}
    </CustomFieldRow>
  )
}
